import React, { useState, useEffect, useMemo } from 'react';
import { TopNavigation } from '@cloudscape-design/components';
import { useRole } from '../contexts/RoleContext';
import { fetchAuthSession } from 'aws-amplify/auth';

interface User {
  username: string;
  email?: string;
  name?: string;
}

interface TopNavBarProps {
  user: User;
  signOut: () => void;
}

export const TopNavBar: React.FC<TopNavBarProps> = ({ user, signOut }) => {
  const { role } = useRole();
  const [displayName, setDisplayName] = useState(user.email || user.name || 'User');
  const [userEmail, setUserEmail] = useState(user.email || '');

  // Extract display name from JWT token on mount
  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken;

        if (idToken) {
          const payload = idToken.payload as any;
          const givenName = payload?.given_name;
          const familyName = payload?.family_name;
          const email = payload?.email;

          if (email) setUserEmail(email);

          // Priority: "First Last" > "First" > email > fallback
          if (givenName && familyName) {
            setDisplayName(`${givenName} ${familyName}`);
          } else if (givenName) {
            setDisplayName(givenName);
          } else if (email) {
            setDisplayName(email);
          }
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    };

    loadUserInfo();
  }, []);

  // Get role badge text
  const roleBadge = useMemo(() => {
    switch (role) {
      case 'Admin':
        return 'Admin';
      case 'Production':
        return 'Production';
      case 'User':
        return 'User';
      default:
        return 'User';
    }
  }, [role]);

  return (
    <div id="top-nav">
      <TopNavigation
        identity={{
          href: '/',
          title: 'Live Maps User Portal'
        }}
        utilities={[
          {
            type: 'menu-dropdown',
            text: displayName,
            description: `${userEmail} • ${roleBadge}`,
            iconName: 'user-profile',
            items: [
              {
                id: 'profile',
                text: 'Profile'
              },
              {
                id: 'orders',
                text: 'Orders & Licenses'
              },
              {
                id: 'divider-1',
                text: '-'
              },
              {
                id: 'signout',
                text: 'Sign out'
              }
            ],
            onItemClick: ({ detail }) => {
              if (detail.id === 'signout') {
                signOut();
              } else if (detail.id === 'profile') {
                window.location.href = '/profile';
              } else if (detail.id === 'orders') {
                window.location.href = '/orders';
              }
            }
          }
        ]}
      />
    </div>
  );
};
